/**
 * Renders the edit layer's pick pass and reads splat indices back.
 *
 * Each pixel holds the index of the front-most splat whose visible footprint
 * covers it (the shader writes the splat index and drops fragments below the
 * pick alpha threshold), or 0xFFFFFFFF where nothing is drawn. Used for click
 * picking, depth-aware ("visible only") selection, the eyedropper, and to find
 * surface points for the sphere brush, measure and orient tools.
 */
import * as pc from 'playcanvas';

export const NO_SPLAT = 0xFFFFFFFF;

export interface IdBuffer {
    /** Pick render target size in pixels. */
    width: number;
    height: number;
    /** Region read back, in render-target pixels (origin top-left). */
    x: number;
    y: number;
    w: number;
    h: number;
    /** w*h splat indices, row-major, top row first. */
    ids: Uint32Array;
}

export class SplatIdPicker {
    private readonly app: pc.AppBase;
    private picker: pc.Picker | null = null;

    constructor(app: pc.AppBase) {
        this.app = app;
    }

    /**
     * Render the pick pass at canvas resolution and read back a region given in
     * normalised coordinates (0..1, origin top-left). Omit the region for the full buffer.
     */
    async read(
        camera: pc.CameraComponent,
        layer: pc.Layer,
        region?: { x: number; y: number; w: number; h: number },
    ): Promise<IdBuffer> {
        const device = this.app.graphicsDevice;
        const width = Math.max(1, Math.floor(device.width));
        const height = Math.max(1, Math.floor(device.height));
        if (!this.picker) this.picker = new pc.Picker(this.app as pc.AppBase, width, height);
        this.picker.resize(width, height);
        this.picker.prepare(camera, this.app.scene, [layer]);

        const nx = region ? Math.max(0, Math.min(1, region.x)) : 0;
        const ny = region ? Math.max(0, Math.min(1, region.y)) : 0;
        const x = Math.floor(nx * width);
        const y = Math.floor(ny * height);
        const w = Math.max(1, Math.min(width - x, Math.ceil((region ? region.w : 1) * width)));
        const h = Math.max(1, Math.min(height - y, Math.ceil((region ? region.h : 1) * height)));

        const rt = (this.picker as unknown as { renderTarget: pc.RenderTarget }).renderTarget;
        const readY = (device as unknown as { isWebGL2?: boolean }).isWebGL2 ? rt.height - (y + h) : y;
        const pixels = await rt.colorBuffer.read(x, readY, w, h, { renderTarget: rt, immediate: true } as never) as Uint8Array;

        const ids = new Uint32Array(w * h);
        const flipRows = !!(device as unknown as { isWebGL2?: boolean }).isWebGL2;
        for (let row = 0; row < h; row++) {
            const srcRow = flipRows ? h - 1 - row : row;
            for (let col = 0; col < w; col++) {
                const p = (srcRow * w + col) * 4;
                ids[row * w + col] = (pixels[p] | (pixels[p + 1] << 8) | (pixels[p + 2] << 16) | (pixels[p + 3] << 24)) >>> 0;
            }
        }
        return { width, height, x, y, w, h, ids };
    }

    destroy(): void {
        this.picker?.destroy();
        this.picker = null;
    }
}

/** Id at a normalised point of a buffer, or NO_SPLAT. Searches `radius` pixels for the nearest hit. */
export function idAt(buf: IdBuffer, nx: number, ny: number, radius = 0): number {
    const px = Math.floor(nx * buf.width) - buf.x;
    const py = Math.floor(ny * buf.height) - buf.y;
    let best = NO_SPLAT;
    let bestD = Infinity;
    for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
            const x = px + dx, y = py + dy;
            if (x < 0 || y < 0 || x >= buf.w || y >= buf.h) continue;
            const id = buf.ids[y * buf.w + x];
            if (id === NO_SPLAT) continue;
            const d = dx * dx + dy * dy;
            if (d < bestD) { bestD = d; best = id; }
        }
    }
    return best;
}

/** Distinct ids in the buffer, optionally limited to pixels where `inside(nx, ny)` holds. */
export function visibleIds(buf: IdBuffer, inside?: (nx: number, ny: number) => boolean): Set<number> {
    const out = new Set<number>();
    for (let row = 0; row < buf.h; row++) {
        const ny = (buf.y + row + 0.5) / buf.height;
        for (let col = 0; col < buf.w; col++) {
            const id = buf.ids[row * buf.w + col];
            if (id === NO_SPLAT) continue;
            if (inside && !inside((buf.x + col + 0.5) / buf.width, ny)) continue;
            out.add(id);
        }
    }
    return out;
}
